import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ClinicalImpression } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Box, Button, Grid, Modal, Stack } from "@mantine/core";

import {
  ClientNote,
  ClientNoteStatus,
  updateClientNoteStatus,
} from "@/clients/zod-schemas/client-note";
import { TextWrapper } from "@/components/TextWrapper";
import { getClinicalImpressionData } from "@/fhir/resource-helpers/clinical-impression";
import PermissionGate from "@/permission/components/PermissionGate";
import { WRITE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { usDateTimeFormat } from "@/utils/datetime";

import { ArchiveModalProps } from "../ClientRemindersNote/ArchiveClientReminderNote";
import { useClientNotes } from "../utils/hooks";

interface DisplayClientConfidentialNoteProps extends ArchiveModalProps {
  setViewFiledConfidential: (refresh: boolean | ((prev: boolean) => boolean)) => void;
}

export default function ArchiveClientConfidentialNote({
  openModal,
  modalHandlers,
  refresh,
  setRefresh,
  setViewFiledConfidential,
}: DisplayClientConfidentialNoteProps) {
  const [restoringNoteId, setRestoringNoteId] = useState<string | null>(null);
  const medplum = useMedplum();
  const { clientId } = useParams<{ clientId: string }>();
  const archivedConfidentialNotes = useClientNotes(
    clientId,
    medplum,
    ClientNoteStatus.Completed,
    refresh,
    ClientNote.Confidential,
  );

  useEffect(() => {
    archivedConfidentialNotes.length > 0 && setViewFiledConfidential(true);
  }, [archivedConfidentialNotes, setViewFiledConfidential]);

  const handleRestoreConfidential = useCallback(
    async (note: ClinicalImpression) => {
      if (restoringNoteId) return;
      setRestoringNoteId(note.id || null);
      try {
        await updateClientNoteStatus({
          medplum,
          clinicalImpression: note,
          status: ClientNoteStatus.InProgress,
        });
        setRefresh((prev) => !prev);

        if (archivedConfidentialNotes.length <= 1) {
          setViewFiledConfidential(false);
        }
      } finally {
        setRestoringNoteId(null);
      }
    },
    [
      archivedConfidentialNotes.length,
      medplum,
      setRefresh,
      setViewFiledConfidential,
      restoringNoteId,
    ],
  );

  return (
    <Modal
      centered
      opened={openModal}
      onClose={modalHandlers.close}
      data-testid="filed-confidential-notes-modal"
      title={<TextWrapper size="xl">Filed Confidential Notes</TextWrapper>}
    >
      {archivedConfidentialNotes.length > 0 ? (
        <Box style={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden", paddingRight: 8 }}>
          <Stack>
            {archivedConfidentialNotes.map((note, index) => {
              const { assessorDisplay, authorDisplay, date, lastUpdated, summary } =
                getClinicalImpressionData(note);
              const archiveDate = usDateTimeFormat(lastUpdated);
              const creationDate = usDateTimeFormat(date);

              return (
                <Grid key={index}>
                  <Grid.Col span={{ base: 12, sm: 9 }}>
                    <TextWrapper
                      size="md"
                      style={{ wordWrap: "break-word" }}
                      data-testid="filed-confidential-note-text"
                    >
                      {summary}
                    </TextWrapper>
                    <TextWrapper pt="xs" size="xs" c="gray.7" fs="italic">
                      Arc: {authorDisplay} {archiveDate}
                    </TextWrapper>
                    <TextWrapper size="xs" c="gray.5" fs="italic">
                      {assessorDisplay} {creationDate}
                    </TextWrapper>
                  </Grid.Col>
                  <PermissionGate allowPatient={false} requiredPermissions={WRITE_PERMISSION_FLAGS}>
                    <Grid.Col span={{ base: 12, sm: 2 }}>
                      <Button
                        color="black"
                        variant="outline"
                        size="compact-xs"
                        disabled={restoringNoteId !== null}
                        loading={restoringNoteId === note.id}
                        data-testid="restore-confidential-note-button"
                        onClick={() => handleRestoreConfidential(note)}
                      >
                        Restore
                      </Button>
                    </Grid.Col>
                  </PermissionGate>
                </Grid>
              );
            })}
          </Stack>
        </Box>
      ) : (
        <TextWrapper fs="italic">No confidential notes filed</TextWrapper>
      )}
    </Modal>
  );
}
